import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, StyleSheet, Text, SafeAreaView, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { Button, useTheme } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../../context/AuthContext';
import { getUserProfile } from '../../api/profileAPI';

const ProfileScreen = ({ navigation }) => {
  const theme = useTheme();
  const { user, signOut, updateUserInfo } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutDialogVisible, setLogoutDialogVisible] = useState(false);
  const lastUpdateTimeRef = useRef(0);
  
  useEffect(() => {
    loadUserProfile();
  }, []);
  
  // useFocusEffect içinde sonsuz döngü sorununu çözdüm
  useFocusEffect(
    useCallback(() => {
      let isMounted = true;
      const currentTime = Date.now();
      
      // Son güncellemeden en az 30 saniye geçtiyse güncelle
      if (currentTime - lastUpdateTimeRef.current > 30000) {
        const fetchUpdatedProfileData = async () => {
          try {
            if (isMounted) {
              const response = await getUserProfile();
              if (response && response.success && response.user) {
                // AuthContext'i en güncel bilgilerle güncelle
                await updateUserInfo(response.user);
                setUserProfile(response.user);
                lastUpdateTimeRef.current = Date.now();
              }
            }
          } catch (error) {
            console.error('Profile data fetch error:', error);
          }
        };
        
        fetchUpdatedProfileData();
      }
      
      return () => {
        isMounted = false;
      };
    }, [updateUserInfo])
  );
  
  const loadUserProfile = async () => {
    try {
      setLoading(true);
      
      // API'den profil bilgilerini al
      const response = await getUserProfile();
      
      if (response && response.success) {
        setUserProfile(response.user);
        // Son güncelleme zamanını set et
        lastUpdateTimeRef.current = Date.now();
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error);
    }
  };
  
  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        <View style={styles.header}>
          <MaterialCommunityIcons 
            name="account-circle" 
            size={100} 
            color="rgb(125, 76, 236)" 
          />
          <Text style={styles.userName}>{user?.name || 'Kullanıcı'} {user?.surname || ''}</Text>
          <Text style={styles.userEmail}>{user?.email || 'kullanici@example.com'}</Text>
        </View>
        
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Hesap Ayarları</Text>
          
          <TouchableOpacity 
            style={styles.menuItem}
            onPress={() => navigation.navigate('EditProfile')}
          >
            <MaterialCommunityIcons name="account-edit" size={24} color="rgb(125, 76, 236)" />
            <Text style={styles.menuItemText}>Profil Düzenle</Text>
            <MaterialCommunityIcons name="chevron-right" size={24} color="rgba(125, 76, 236, 0.5)" style={styles.menuItemIcon} />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.menuItem}
            onPress={() => navigation.navigate('ChangePassword')}
          >
            <MaterialCommunityIcons name="lock-reset" size={24} color="rgb(125, 76, 236)" />
            <Text style={styles.menuItemText}>Şifre Değiştir</Text>
            <MaterialCommunityIcons name="chevron-right" size={24} color="rgba(125, 76, 236, 0.5)" style={styles.menuItemIcon} />
          </TouchableOpacity>
        </View>
        
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Uygulama</Text>
          
          <TouchableOpacity 
            style={styles.menuItem}
            onPress={() => navigation.navigate('About')}
          >
            <MaterialCommunityIcons name="information-outline" size={24} color="rgb(125, 76, 236)" />
            <Text style={styles.menuItemText}>Hakkında</Text>
            <MaterialCommunityIcons name="chevron-right" size={24} color="rgba(125, 76, 236, 0.5)" style={styles.menuItemIcon} />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.menuItem}
            onPress={() => setLogoutDialogVisible(true)}
          >
            <MaterialCommunityIcons name="logout" size={24} color="rgb(210, 50, 80)" />
            <Text style={[styles.menuItemText, { color: 'rgb(210, 50, 80)' }]}>Çıkış Yap</Text>
          </TouchableOpacity>
        </View>
        
        <View style={styles.footer}>
          <Text style={styles.version}>Versiyon 1.0.0</Text>
        </View>
      </ScrollView>
      
      {/* Çıkış Yapma Modal */}
      <Modal
        visible={logoutDialogVisible}
        transparent={true}
        onRequestClose={() => setLogoutDialogVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <MaterialCommunityIcons 
              name="logout" 
              size={50} 
              color="rgb(125, 76, 236)"
              style={styles.modalIcon} 
            />
            
            <Text style={styles.modalTitle}>Çıkış Yap</Text>
            <Text style={styles.modalText}>Çıkış yapmak istediğinize emin misiniz?</Text>
            
            <View style={styles.modalButtonContainer}>
              <Button 
                mode="contained" 
                onPress={() => {
                  setLogoutDialogVisible(false);
                  handleLogout();
                }}
                style={[styles.modalButton, { backgroundColor: 'rgb(210, 50, 80)' }]}
                icon="logout"
              >
                Çıkış Yap
              </Button>
              <Button 
                mode="outlined" 
                onPress={() => setLogoutDialogVisible(false)}
                style={styles.modalButton}
              >
                İptal
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'rgb(232, 162, 249)',
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    width: '80%',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    elevation: 4,
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  modalIcon: {
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(125, 76, 236)',
  },
  modalText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 20,
    color: 'rgb(90, 50, 180)',
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  modalButton: {
    marginHorizontal: 5,
    minWidth: 120,
  },
  header: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgb(232, 162, 249)',
    marginBottom: 20,
  },
  userName: {
    fontSize: 24,
    fontWeight: 'bold',
    marginTop: 10,
    color: 'rgb(125, 76, 236)',
  },
  userEmail: {
    fontSize: 16,
    fontWeight: 'bold',
    color: 'rgb(90, 50, 180)',
    marginTop: 5,
  },
  section: {
    backgroundColor: '#fff',
    padding: 15,
    marginHorizontal: 15,
    marginBottom: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(125, 76, 236, 0.3)',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    color: 'rgb(125, 76, 236)',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(125, 76, 236, 0.1)',
  },
  menuItemText: {
    fontSize: 16,
    marginLeft: 15,
    flex: 1,
    color: 'rgb(90, 50, 180)',
  },
  menuItemIcon: {
    marginLeft: 'auto',
  },
  footer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  version: {
    fontSize: 12,
    color: 'rgb(90, 50, 180)',
  }
});

export default ProfileScreen;
